import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Link,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { type RegisterPayload } from "../utils/api";
import { LOGO_URL } from "../constants/brand";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { registerUser } from "../features/auth/authSlice";

type RegisterFormState = RegisterPayload & { confirmPassword: string };

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    status,
    error: authError,
    token,
  } = useAppSelector((state) => state.auth);
  const [formValues, setFormValues] = useState<RegisterFormState>({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    address: "",
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isLoading = status === "loading";

  useEffect(() => {
    if (token) {
      navigate("/user", { replace: true });
    }
  }, [token, navigate]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (formValues.password !== formValues.confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }

    try {
      const { confirmPassword, ...payload } = formValues;
      await dispatch(registerUser(payload)).unwrap();
      navigate("/user");
    } catch {
      // Error feedback handled inside the thunk via toast
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        bgcolor: "background.default",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 4, md: 6 },
          width: "100%",
          maxWidth: 800,
          borderRadius: 1,
          border: "1px solid rgba(0,0,0,0.05)",
          boxShadow: "0 10px 50px rgba(0,0,0,0.04)",
          bgcolor: "background.paper",
        }}
      >
        <Stack spacing={4}>
          <Box sx={{ textAlign: "center" }}>
            <Box
              component="img"
              src={LOGO_URL}
              alt="Logo"
         sx={{ height: 70, mb: 3, objectFit: 'contain' }}
            />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Create your account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your information below to join Limitless.
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2.5,
                }}
              >
                <TextField
                  label="Full name"
                  name="name"
                  required
                  fullWidth
                  autoComplete="name"
                  value={formValues.name}
                  onChange={handleChange}
                />
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  required
                  fullWidth
                  autoComplete="email"
                  value={formValues.email}
                  onChange={handleChange}
                />
                <Box>
                  <PhoneInput
                    country="us"
                    value={formValues.phoneNumber}
                    onChange={(value) =>
                      setFormValues((prev) => ({ ...prev, phoneNumber: value }))
                    }
                    inputProps={{
                      name: "phoneNumber",
                      required: true,
                      autoComplete: "tel",
                    }}
                    inputStyle={{
                      width: "100%",
                      height: 56, // Match MUI large height
                      fontSize: 16,
                      borderRadius: "4px",
                      border: "1px solid #e5e7eb",
                      backgroundColor: "#ffffff",
                      paddingLeft: 48,
                    }}
                    buttonStyle={{
                      borderRadius: "4px 0 0 4px",
                      border: "1px solid #e5e7eb",
                      backgroundColor: "transparent",
                    }}
                    containerStyle={{
                      width: "100%",
                    }}
                  />
                </Box>
                <TextField
                  label="Address"
                  name="address"
                  required
                  fullWidth
                  autoComplete="street-address"
                  value={formValues.address}
                  onChange={handleChange}
                />
                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  fullWidth
                  autoComplete="new-password"
                  value={formValues.password}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Confirm password"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  fullWidth
                  autoComplete="new-password"
                  value={formValues.confirmPassword}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {(formError || authError) && (
                <Alert severity="error">{formError ?? authError}</Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 700,
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.2)",
                  mt: 1,
                }}
              >
                {isLoading ? "Creating account..." : "Create Account"}
              </Button>

              <Typography variant="body2" textAlign="center">
                Already have an account?{" "}
                <Link
                  component={RouterLink}
                  to="/login"
                  fontWeight={600}
                  underline="none"
                >
                  Log in
                </Link>
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default RegisterPage;
